/**
 * Employee Details Page - Microsoft 365 Style
 * Displays comprehensive employee information
 */

import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { makeStyles, Button, Text, Avatar, Spinner, Badge } from '@fluentui/react-components';
import { ArrowLeftRegular, EditRegular } from '@fluentui/react-icons';
import { ContentCard } from '../../components/layout';
import {
  ApiClient,
  newToast,
  fullDate,
  getEmployeeImageUrl,
} from '../../configurations/index.config';
import { getErrorMessage, Employee } from '../../types';

const useStyles = makeStyles({
  container: {
    maxWidth: '1200px',
  },
  header: {
    marginBottom: '24px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  headerLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
  },
  backButton: {
    minWidth: 'auto',
  },
  pageTitle: {
    fontSize: '24px',
    fontWeight: '600',
    color: '#323130',
  },
  headerActions: {
    display: 'flex',
    gap: '8px',
  },
  employeeHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '20px',
    padding: '24px',
    borderBottom: '1px solid #e1dfdd',
  },
  employeeName: {
    fontSize: '20px',
    fontWeight: '600',
    color: '#323130',
    marginBottom: '4px',
  },
  employeeRole: {
    fontSize: '14px',
    color: '#605e5c',
  },
  contentGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
    gap: '24px',
    padding: '24px',
  },
  section: {
    marginBottom: '0',
  },
  sectionTitle: {
    fontSize: '16px',
    fontWeight: '600',
    color: '#323130',
    marginBottom: '16px',
  },
  fieldGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  field: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
  },
  fieldLabel: {
    fontSize: '12px',
    fontWeight: '600',
    color: '#605e5c',
    textTransform: 'uppercase',
  },
  fieldValue: {
    fontSize: '14px',
    color: '#323130',
  },
  emptyState: {
    padding: '60px 20px',
    textAlign: 'center',
    color: '#605e5c',
  },
  statusBadge: {
    display: 'inline-block',
    padding: '4px 12px',
    borderRadius: '4px',
    fontSize: '12px',
    fontWeight: '500',
  },
  statusActive: {
    backgroundColor: '#d1f0e7',
    color: '#107c10',
  },
  statusInactive: {
    backgroundColor: '#f3f2f1',
    color: '#605e5c',
  },
});

export const EmployeeDetailsFluentUI: React.FC = () => {
  const styles = useStyles();
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams<{ id: string }>();

  const [employee, setEmployee] = useState<Employee | null>(location.state?.employee || null);
  const [loading, setLoading] = useState(!location.state?.employee);

  const fetchEmployeeDetails = useCallback(async () => {
    setLoading(true);
    try {
      const response = await ApiClient.get('/get-employee-data');
      if (response.data.status === 'success') {
        const employees = response.data.data || [];
        const foundEmployee = employees.find((emp: Employee) => emp.id === Number(id));

        if (foundEmployee) {
          console.log('Employee Data from API:', foundEmployee);
          setEmployee(foundEmployee);
        } else {
          newToast('error', 'Employee not found');
          navigate('/account/employees');
        }
      } else {
        newToast('error', response.data?.message || 'Failed to fetch employee details');
      }
    } catch (error: unknown) {
      newToast('error', getErrorMessage(error));
    }
    setLoading(false);
  }, [id, navigate]);

  useEffect(() => {
    // If employee data wasn't passed via navigation state, fetch it
    if (!employee && id) {
      fetchEmployeeDetails();
    }
  }, [employee, id, fetchEmployeeDetails]);

  const handleBack = () => {
    navigate('/account/employees');
  };

  const handleEdit = () => {
    if (employee) {
      navigate(`/account/add-employee`, { state: { employee } });
    }
  };

  if (loading) {
    return (
      <div className={styles.container}>
        <div className={styles.emptyState}>
          <Spinner size="large" label="Loading employee details..." />
        </div>
      </div>
    );
  }

  if (!employee) {
    return (
      <div className={styles.container}>
        <div className={styles.emptyState}>
          <Text size={500} weight="semibold">
            Employee not found
          </Text>
          <Button appearance="primary" onClick={handleBack} style={{ marginTop: '16px' }}>
            Back to Employees
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      {/* Header */}
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <Button
            icon={<ArrowLeftRegular />}
            appearance="subtle"
            onClick={handleBack}
            className={styles.backButton}
          />
          <Text className={styles.pageTitle}>Employee Details</Text>
        </div>
        <div className={styles.headerActions}>
          <Button icon={<EditRegular />} appearance="primary" onClick={handleEdit}>
            Edit Employee
          </Button>
        </div>
      </div>

      {/* Employee Card */}
      <ContentCard>
        {/* Employee Header */}
        <div className={styles.employeeHeader}>
          <Avatar
            name={`${employee.first_name} ${employee.surname}`}
            image={employee.face_url ? { src: getEmployeeImageUrl(employee.face_url) } : undefined}
            color="brand"
            size={72}
          />
          <div>
            <Text weight="semibold" size={500}>
              {employee.first_name} {employee.surname}
              {employee.other_names && ` (${employee.other_names})`}
            </Text>
            <Text className={styles.employeeRole}>
              {employee.role_name || 'No role assigned'}
              {employee.department_name && ` • ${employee.department_name}`}
            </Text>
            <Badge appearance="filled" className={styles.statusActive}>
              Active
            </Badge>
          </div>
        </div>

        {/* Employee Details Grid */}
        <div className={styles.contentGrid}>
          {/* Personal Information */}
          <div className={styles.section}>
            <Text className={styles.sectionTitle}>Personal Information</Text>
            <div className={styles.fieldGroup}>
              <div className={styles.field}>
                <Text className={styles.fieldLabel}>Employee ID</Text>
                <Text className={styles.fieldValue}>{employee.unique_emp_id || employee.id}</Text>
              </div>
              <div className={styles.field}>
                <Text className={styles.fieldLabel}>First Name</Text>
                <Text className={styles.fieldValue}>{employee.first_name}</Text>
              </div>
              <div className={styles.field}>
                <Text className={styles.fieldLabel}>Last Name</Text>
                <Text className={styles.fieldValue}>
                  {employee.surname || (employee as any).last_name || 'N/A'}
                </Text>
              </div>
              {employee.other_names && (
                <div className={styles.field}>
                  <Text className={styles.fieldLabel}>Other Names</Text>
                  <Text className={styles.fieldValue}>{employee.other_names}</Text>
                </div>
              )}
              {employee.date_of_birth && (
                <div className={styles.field}>
                  <Text className={styles.fieldLabel}>Date of Birth</Text>
                  <Text className={styles.fieldValue}>{fullDate(employee.date_of_birth)}</Text>
                </div>
              )}
            </div>
          </div>

          {/* Contact Information */}
          <div className={styles.section}>
            <Text className={styles.sectionTitle}>Contact Information</Text>
            <div className={styles.fieldGroup}>
              <div className={styles.field}>
                <Text className={styles.fieldLabel}>Email Address</Text>
                <Text className={styles.fieldValue}>
                  <a
                    href={`mailto:${employee.email}`}
                    style={{ color: '#0078d4', textDecoration: 'none' }}
                  >
                    {employee.email}
                  </a>
                </Text>
              </div>
              <div className={styles.field}>
                <Text className={styles.fieldLabel}>Phone Number</Text>
                <Text className={styles.fieldValue}>
                  {employee.contact_one || (employee as any).phone || 'N/A'}
                </Text>
              </div>
            </div>
          </div>

          {/* Employment Information */}
          <div className={styles.section}>
            <Text className={styles.sectionTitle}>Employment Information</Text>
            <div className={styles.fieldGroup}>
              {employee.role_name && (
                <div className={styles.field}>
                  <Text className={styles.fieldLabel}>Role</Text>
                  <Text className={styles.fieldValue}>{employee.role_name}</Text>
                </div>
              )}
              {employee.department_name && (
                <div className={styles.field}>
                  <Text className={styles.fieldLabel}>Department</Text>
                  <Text className={styles.fieldValue}>{employee.department_name}</Text>
                </div>
              )}
              <div className={styles.field}>
                <Text className={styles.fieldLabel}>Country</Text>
                <Text className={styles.fieldValue}>{employee.country || 'N/A'}</Text>
              </div>
            </div>
          </div>
        </div>
      </ContentCard>
    </div>
  );
};

export default EmployeeDetailsFluentUI;
